/* LEVEL 5 — 刀房：第二關續（emergency laparoscopic salpingectomy; sterile technique, camera, time-out, count） */
import {
  addWait,
  annoy,
  ask,
  C,
  type Cell,
  chat,
  type ChoiceItem,
  clearWait,
  clock,
  closeZoom,
  type Ctx,
  dither,
  doc,
  ent,
  eventBanner,
  findEnt,
  finding,
  flag,
  type Hotspot,
  type Level,
  leaveBy,
  me,
  mkEnt,
  type MonitorData,
  note,
  obox,
  openMonitor,
  P,
  pell,
  pline,
  playEnding,
  post,
  R,
  session,
  sessionGuard,
  spend,
  strList,
  T,
  tr,
  walkEnt,
} from "../../engine";
import {
  type CamCmd,
  type CamCmdDef,
  type LapState,
  makeScope,
  newLap,
  type Tip,
  trackCmd,
} from "../shared/laparoscope";
import {
  contaminate,
  drawOrTable,
  gownChoice,
  guardTouch,
  scrubSinkDialog,
  type SterileVars,
  type TimeoutItem,
  timeoutChoices,
} from "../shared/sterile";

/* ---------- static data ---------- */

const FACTS = {
  secret: tr(
    "曉婷拜託不要讓媽媽知道她懷孕、有男朋友",
    "Hsiao-Ting begs that her mom not find out she is pregnant or has a boyfriend",
  ),
  fertility: tr("曉婷擔心以後不能生小孩", "Hsiao-Ting worries she won't be able to have children"),
  scared: tr("曉婷很怕醒不過來", "Hsiao-Ting is terrified she won't wake up"),
  npo: tr("晚上六點吃了一碗麵", "Ate a bowl of noodles at 18:00"),
} as const;
type FactId = keyof typeof FACTS;
const isFact = (x: string): x is FactId => x in FACTS;

/** Interactions that break sterility once gowned (or re-dirty scrubbed hands). */
const NONSTERILE = ["scrub", "anesth", "ormon", "tower", "kick", "board", "erchart"];

interface Gauze {
  x: number;
  y: number;
  where: string;
  h1: string;
  h2: string;
}

const GAUZE: Gauze[] = [
  {
    x: 161,
    y: 119,
    where: tr("子宮後面的 cul-de-sac", "the cul-de-sac behind the uterus"),
    h1: tr("看低的地方。血會往哪裡流？", "Look low. Where does blood run to?"),
    h2: tr("子宮後面，cul-de-sac，拉近看。", "Behind the uterus, the cul-de-sac. Zoom in."),
  },
  {
    x: 42,
    y: 102,
    where: tr("右側結腸旁溝", "the right paracolic gutter"),
    h1: tr("右邊旁溝看了嗎？", "Did you check the right gutter?"),
    h2: tr("畫面左下，右側結腸旁溝，拉近看。", "Lower left of the screen, right paracolic gutter. Zoom in."),
  },
  {
    x: 236,
    y: 100,
    where: tr("左側卵巢下方", "under the left ovary"),
    h1: tr("左邊卵巢附近翻一下。", "Take a look around the left ovary."),
    h2: tr("畫面右邊，左側卵巢下面，拉近看。", "Right side of the screen, under the left ovary. Zoom in."),
  },
  {
    x: 182,
    y: 30,
    where: tr("腸子之間", "between the bowel loops"),
    h1: tr("腸子之間也要看。", "Check between the bowel loops too."),
    h2: tr("畫面上方偏右，腸子中間，拉近看。", "Upper right of the screen, between the bowel loops. Zoom in."),
  },
];

const DECOYS = [
  { x: 206, y: 110, say: tr("那是脂肪。", "That’s fat.") },
  { x: 112, y: 22, say: tr("那是腸子反光。", "That’s light glinting off bowel.") },
  { x: 96, y: 114, say: tr("那是血塊，吸掉就沒了。", "That’s a clot. Suction it and it’s gone.") },
  { x: 60, y: 40, say: tr("那是網膜。", "That’s omentum.") },
];

type Target = "R" | "L" | "C";
/** Camera targets in world coords. */
const TG: Record<Target, { x: number; y: number }> = {
  R: { x: 82, y: 62 },
  L: { x: 220, y: 62 },
  C: { x: 150, y: 112 },
};

type Cmd = CamCmd<Target>;

/** Camera commands keyed by surgery minute (rel). */
const CMDS: Record<number, CamCmdDef<Target>> = {
  12: { type: "look", tg: "R", text: tr("看右邊附屬器。", "Show me the right adnexa.") },
  14: { type: "out", text: tr("太近了，退一點。", "Too close. Back out a bit.") },
  17: { type: "wipe", text: tr("起霧了，看不到。", "It’s fogged up. I can’t see.") },
  19: {
    type: "look",
    tg: "C",
    text: tr("往骨盆深一點，看 cul-de-sac。", "Deeper into the pelvis. Show me the cul-de-sac."),
  },
  22: { type: "look", tg: "L", text: tr("看左邊，對側輸卵管。", "Go left. The contralateral tube.") },
  25: {
    type: "look",
    tg: "R",
    minZ: 1.3,
    text: tr("回右邊，靠近一點，我要燒了。", "Back to the right, closer. I’m going to cauterize."),
  },
  27: { type: "out", smoke: true, text: tr("煙太多，退一點。", "Too much smoke. Back out a bit.") },
};

// Questions double as QA keys, so each is resolved once and used on both sides.
const Q_SITE = tr("子宮外孕最常發生在輸卵管哪一段？", "Which part of the tube is the most common site of an ectopic?");
const Q_MTX = tr("她為什麼不考慮用 methotrexate？", "Why isn’t methotrexate an option for her?");
const Q_CONTRA = tr("為什麼一定要看對側輸卵管？", "Why do we always have to look at the contralateral tube?");
const QS: Record<number, string> = {
  13: Q_SITE,
  20: Q_MTX,
  26: Q_CONTRA,
};
const QA: Record<string, string> = {
  [Q_SITE]: tr("壺腹部（ampulla），約七到八成", "The ampulla, about 70 to 80 percent"),
  [Q_MTX]: tr(
    "破裂、腹內出血、血行動力學不穩定是 MTX 禁忌",
    "Rupture, hemoperitoneum and hemodynamic instability are contraindications to MTX",
  ),
  [Q_CONTRA]: tr(
    "評估對側是否正常，影響未來生育與這次術式選擇（切除或保留）",
    "To see whether the other side is normal; it affects future fertility and the choice of procedure now (salpingectomy or tube-sparing)",
  ),
};

type StepKind = "suction" | "probe" | "grasp" | "burn" | "cut" | "extract" | "irrigate";
const STEPS: [rel: number, name: string, kind: StepKind, narr: string][] = [
  [
    12,
    tr("吸除血塊", "Evacuate clots"),
    "suction",
    tr(
      "小周把骨盆腔的血塊一團一團吸掉，視野慢慢清楚了。",
      "Chou suctions the clots out of the pelvis one by one. The view slowly clears.",
    ),
  ],
  [
    16,
    tr("找出血點", "Find the bleeder"),
    "probe",
    tr(
      "主任用夾子撥開腸子，找到右側輸卵管壺腹部的破口，還在滲血。",
      "Dr. Lin moves the bowel aside with a grasper and finds the rupture in the ampulla of the right tube, still oozing.",
    ),
  ],
  [
    19,
    tr("抓起右側輸卵管", "Grasp the right tube"),
    "grasp",
    tr("主任夾起右側輸卵管，把它往上提。", "Dr. Lin grasps the right tube and lifts it up."),
  ],
  [
    23,
    tr("燒灼輸卵管繫膜", "Cauterize the mesosalpinx"),
    "burn",
    tr(
      "主任：「Bipolar。」滋——一陣白煙，繫膜一段一段燒過去。",
      "Dr. Lin: “Bipolar.” A sizzle and a puff of white smoke; the mesosalpinx is burned through section by section.",
    ),
  ],
  [
    26,
    tr("切除右側輸卵管", "Excise the right tube"),
    "cut",
    tr("主任：「剪。」右側輸卵管被剪了下來。", "Dr. Lin: “Scissors.” The right tube is cut free."),
  ],
  [
    27,
    tr("取出檢體", "Remove the specimen"),
    "extract",
    tr(
      "小周把切下來的輸卵管從左下的 trocar 拉出來，交給阿珍。",
      "Chou pulls the tube out through the lower-left trocar and hands it to Jen.",
    ),
  ],
  [
    29,
    tr("沖洗、檢查", "Irrigate and inspect"),
    "irrigate",
    tr("小周用生理食鹽水沖洗骨盆腔，再吸乾淨。", "Chou irrigates the pelvis with saline, then suctions it dry."),
  ],
];

/* ---------- session ---------- */

type Phase = "preop" | "timeout" | "lap" | "count" | "close" | "post";
type Judge = "correct" | "partial" | "wrong" | "none" | "skipped";
const isGrade = (x: unknown): x is "correct" | "partial" | "wrong" =>
  x === "correct" || x === "partial" || x === "wrong";

interface Vars extends SterileVars {
  trust: number;
  qToR2: boolean;
  offerSuture: boolean;
  sutured: boolean;
  step: number | null;
  phase: Phase;
  asleep: boolean;
  draped: boolean;
  salpDone: boolean;
  timeout: { done: boolean; withClerk: boolean; ans: Record<string, string> };
  cmd: Cmd | null;
  cam: { hit: number; miss: number; wrong: number };
  q: string | null;
  qAt: number | null;
  qRes: { q: string; j: Judge }[];
  count: { at: number | null; resolved: boolean; tries: string[]; by: string | null };
  postAt: number | null;
  motherPost: number;
  motherPre: number;
  disclosed: boolean;
  revealed: Set<FactId>;
  anesAsked: boolean;
  lap: LapState;
  tableAnim: { t0: number } | null;
  tableGone: boolean;
  warnedTable: string;
  gauze: Gauze;
  picks: string[];
  xrayAt: number | null;
  xrayKnown: boolean;
  lastMissAt: number;
  lapAt: number | null;
  r2Answer: { t: number; a: string } | null;
  r2Search: number | null;
  closeAt: number | null;
  erRead: boolean;
  endQueued: boolean;
}

const S = session<Vars>();
const DOOR: Cell = [13, 11];

const inOR = (): boolean => P.x >= 9;
const curStep = () => (S.step != null && S.step >= 0 ? STEPS[S.step] : undefined);
const relNow = (t: number): number => t - (S.lapAt ?? t) + 11;

/* ---------- laparoscope ---------- */

const scope = makeScope({
  lap: () => S.lap,
  worldKey: () => [S.salpDone, S.phase === "count" && !S.count.resolved, S.phase, S.step].join("|"),
  paintWorld,
  // The hidden gauze has to stay findable.
  textureKeep: ["#b8505a", "#d88a90", "#f4e6e6", "#5a1018"],
  tips: toolTips,
  overlay(c, now) {
    if (
      S.cmd?.type === "look" &&
      S.cmd.tg === "R" &&
      S.cmd.minZ &&
      S.lap.smoke < 0.2 &&
      Math.floor(now / 200) % 3 === 0
    )
      R(c, "#ffffff", 64, 66, 3, 3);
  },
  hud(c) {
    if (S.step != null && S.step >= 0) {
      const n = S.step + 1;
      const tot = STEPS.length;
      for (let i = 0; i < tot; i++) obox(c, i < n ? "#7fe08a" : "#20263a", 150 + i * 5, 100, 3, 3);
    }
  },
  caption() {
    const st = curStep();
    return st && S.step != null
      ? tr(`手術進度 ${S.step + 1}/${STEPS.length}：${st[1]}`, `Step ${S.step + 1}/${STEPS.length}: ${st[1]}`)
      : tr("你扶著鏡頭", "You’re on the camera");
  },
  hot: lapHot,
  onFrame: evalCmd,
  onWipe() {
    post(
      "table",
      "narr",
      null,
      tr("你把鏡頭抽出來擦乾淨，再放回去。", "You pull the scope out, wipe the lens and put it back."),
      { silent: true },
    );
    if (S.cmd?.type === "wipe") S.cmd.wiped = true;
  },
});
const lapCfg = scope.cfg;
const openLap = scope.open;

function paintWorld(c: Ctx): void {
  const E = (x: number, y: number, rx: number, ry: number, col: string) => pell(c, x, y, rx, ry, col);
  c.fillStyle = "#4e121b";
  c.fillRect(0, 0, 300, 150);
  E(150, 82, 148, 74, "#6a1c26");
  E(150, 82, 118, 58, "#7e2630");
  E(150, 82, 90, 46, "#8e3038");
  dither(c, 0, 0, 300, 150, "#5a1620", 0.18);
  for (let i = 0; i < 7; i++) {
    E(20 + i * 44, 16, 27, 15, "#a85a5e");
    E(20 + i * 44, 16, 26, 14, "#d98b8b");
    E(14 + i * 44, 12, 10, 5, "#efb0a8");
  }
  pline(c, 126, 72, 108, 60, "#a85a62", 8);
  pline(c, 126, 72, 108, 60, "#d68a8f", 6);
  if (!S.salpDone) {
    pline(c, 108, 60, 92, 62, "#a85a62", 8);
    pline(c, 108, 60, 92, 62, "#d68a8f", 6);
  }
  pline(c, 174, 72, 196, 58, "#a85a62", 8);
  pline(c, 174, 72, 196, 58, "#d68a8f", 6);
  pline(c, 196, 58, 214, 62, "#d68a8f", 6);
  pline(c, 132, 90, 104, 104, "#c06c74", 2);
  pline(c, 168, 90, 196, 104, "#c06c74", 2);
  E(150, 80, 31, 25, "#b86a6e");
  E(150, 80, 30, 24, "#e9a3a3");
  E(141, 71, 12, 8, "#f6c3c0");
  dither(c, 122, 88, 56, 14, "#c98688", 0.4);
  if (!S.salpDone) {
    E(82, 62, 16, 13, "#2e0c1c");
    E(82, 62, 15, 12, "#4e1630");
    E(78, 58, 6, 4, "#7a2a4a");
    R(c, "#c0202e", 80, 74, 3, 6);
    R(c, "#c0202e", 86, 73, 2, 9);
    E(84, 86, 8, 3, "#a0141f");
  } else {
    E(112, 66, 4, 3, "#a85060");
    R(c, "#d9dcea", 110, 63, 1, 6);
    R(c, "#d9dcea", 114, 63, 1, 6);
  }
  E(78, 86, 12, 8, "#c99a9a");
  E(78, 86, 11, 7, "#f0c8c8");
  E(222, 86, 12, 8, "#c99a9a");
  E(222, 86, 11, 7, "#f0c8c8");
  E(226, 60, 6, 4, "#e0a0a8");
  E(150, 114, 42, 15, "#3a0a12");
  E(150, 111, 30, 8, "#4a0d16");
  dither(c, 110, 104, 80, 20, "#5a1018", 0.3);
  const clots = S.phase === "lap" ? Math.max(1, 6 - Math.max(0, S.step ?? 0)) : 1;
  for (let i = 0; i < clots; i++) E(100 + i * 18, 100 + (i % 2) * 10, 5, 3, "#2a060c");
  E(206, 110, 7, 5, "#c9a85a");
  E(204, 108, 4, 3, "#e6c876");
  E(210, 112, 3, 2, "#e6c876");
  R(c, "#fff6f0", 110, 21, 2, 1);
  R(c, "#fff6f0", 113, 22, 1, 1);
  E(96, 114, 7, 4, "#2a060c");
  E(60, 40, 9, 5, "#e0b08a");
  dither(c, 51, 35, 18, 10, "#c99070", 0.4);
  if (S.phase === "count" && !S.count.resolved) {
    const g = S.gauze;
    const gx = g.x - 5;
    const gy = g.y - 4;
    R(c, "#5a1018", gx - 1, gy - 1, 12, 9);
    R(c, "#b8505a", gx, gy, 10, 7);
    for (let i = 0; i < 10; i += 2) R(c, "#d88a90", gx + i, gy, 1, 7);
    for (let j = 0; j < 7; j += 2) R(c, "#d88a90", gx, gy + j, 10, 1);
    R(c, "#f4e6e6", gx + 7, gy, 3, 2);
    R(c, "#ffffff", gx + 8, gy, 1, 1);
    E(g.x - 3, g.y + 2, 5, 3, "#2a060c");
  }
}

function lapHot(): Hotspot[] {
  if (!(S.phase === "count" && !S.count.resolved)) return [];
  const { toS, inView } = scope;
  const hs: Hotspot[] = [];
  const g = toS(S.gauze.x, S.gauze.y);
  const label = tr("指給主任看這裡", "Show Dr. Lin this spot");
  if (inView(g)) hs.push({ x: g[0] - 10, y: g[1] - 8, w: 20, h: 16, label, fn: () => pick("gauze") });
  DECOYS.forEach((dc, i) => {
    const p = toS(dc.x, dc.y);
    if (inView(p)) hs.push({ x: p[0] - 9, y: p[1] - 7, w: 18, h: 14, label, fn: () => pick(i) });
  });
  hs.push({ x: 0, y: 0, w: 192, h: 108, label, fn: () => pick(null) });
  return hs;
}

function pick(what: "gauze" | number | null): void {
  const lp = S.lap;
  if (lp.fog > 0.5) {
    post(
      "table",
      "npc",
      "vs",
      tr("霧成這樣你看得到什麼？先擦鏡頭。", "What can you see through that fog? Wipe the lens first."),
    );
    return;
  }
  if (what === "gauze") {
    if (lp.z < 1.3) {
      post(
        "table",
        "npc",
        "vs",
        tr("那邊好像有東西……太遠了，拉近一點看清楚。", "Something might be there… too far. Zoom in so we can see."),
      );
      return;
    }
    clearWait("findg");
    S.picks.push(tr("找到", "found"));
    resolveCount(
      tr(
        `你把鏡頭拉近：${S.gauze.where}有一塊吸滿血的紗布，一角還露出白色。小周把它夾了出來。`,
        `You zoom in: in ${S.gauze.where} there is a blood-soaked sponge, one white corner still showing. Chou grasps it and pulls it out.`,
      ),
      S.xrayKnown
        ? tr("照 X 光後用鏡頭找到", "found with the camera after the X-ray")
        : tr("你自己用鏡頭找到", "you found it yourself with the camera"),
    );
    closeZoom();
    return;
  }
  if (typeof what === "number") {
    S.picks.push(tr("誤認", "misidentified"));
    post("table", "npc", "vs", (DECOYS[what]?.say ?? "") + tr("再找。", " Keep looking."));
    return;
  }
  if (S.t - S.lastMissAt >= 1) {
    S.lastMissAt = S.t;
    S.picks.push(tr("空", "nothing there"));
    post("table", "npc", "vs", tr("那邊沒東西。", "There’s nothing there."));
  }
}

function evalCmd(dt: number): void {
  const cmd = S.cmd;
  if (!cmd || !S.atCamera) return;
  const decoy = cmd.tg === "R" ? "L" : cmd.tg === "L" ? "R" : null;
  const { hit, wrong } = trackCmd(cmd, S.lap, dt, TG, decoy);
  if (wrong) {
    S.cam.wrong++;
    post(
      "table",
      "npc",
      cmd.tg === "R" ? "r2" : "vs",
      cmd.tg === "R"
        ? tr("學弟，病人的右邊在畫面左邊啦。", "Hey, the patient’s right is on the left of the screen.")
        : tr("那是有病灶的那側，我要看對側。", "That’s the diseased side. I want the other side."),
    );
  }
  if (hit) {
    S.cam.hit++;
    S.cmd = null;
    clearWait("cam");
    flag("vs");
    post(
      "table",
      "npc",
      "vs",
      tr(["好。", "嗯。", "對。", "好，就這樣。"], ["Good.", "Mm.", "Right.", "Good, hold it there."])[S.cam.hit % 4] ??
        tr("好。", "Good."),
    );
  }
}

function stepNow(): number | null {
  if (S.lapAt == null) return null;
  const rel = S.t - S.lapAt + 11;
  let cur: number | null = null;
  STEPS.forEach((st, i) => {
    if (rel >= st[0]) cur = i;
  });
  return cur;
}

function toolTips(now: number): { L: Tip; R: Tip } {
  const k = curStep()?.[2] ?? null;
  const tt = now / 1000;
  const tips: { L: Tip; R: Tip } = {
    L: { x: 118, y: 92, kind: "grasp", open: false },
    R: { x: 178, y: 84, kind: "grasp", open: false },
  };
  if (k === "suction" || k === "irrigate")
    tips.L = {
      x: 150 + 26 * Math.sin(tt * 0.9),
      y: 110 + 5 * Math.cos(tt * 1.4),
      kind: "suction",
      water: k === "irrigate",
    };
  if (k === "probe")
    tips.L = {
      x: 96 + 6 * Math.sin(tt * 1.6),
      y: 66 + 3 * Math.cos(tt * 1.1),
      kind: "grasp",
      open: Math.floor(tt * 2) % 2 === 0,
    };
  if (k === "grasp") {
    tips.L = { x: 92, y: 58 + 2 * Math.sin(tt * 2), kind: "grasp", open: false, hold: true };
    tips.R = { x: 120 - 8 * Math.min(1, (tt % 6) / 6), y: 74, kind: "bipolar" };
  }
  if (k === "burn") {
    tips.L = { x: 92, y: 56, kind: "grasp", hold: true };
    tips.R = { x: 104 + 4 * Math.sin(tt * 0.7), y: 70, kind: "bipolar", spark: Math.floor(now / 180) % 3 === 0 };
  }
  if (k === "cut") {
    tips.L = { x: 92, y: 56, kind: "grasp", hold: true };
    tips.R = { x: 108, y: 68, kind: "scissors", open: Math.floor(tt * 3) % 2 === 0 };
  }
  if (k === "extract") {
    const f = (tt % 4) / 4;
    tips.L = { x: 92 - 60 * f, y: 60 + 40 * f, kind: "grasp", hold: true, specimen: true };
  }
  return tips;
}

function issueCmd(rel: number): void {
  const c = CMDS[rel];
  if (!c) return;
  if (S.atCamera) {
    if (S.cmd) S.cam.miss++;
    S.cmd = { ...c, t: S.t };
    if (c.smoke) S.lap.smoke = 0.9;
    post("table", "npc", "vs", c.text);
    addWait({ id: "cam", text: tr("鏡頭：", "Camera: ") + c.text, untilT: S.t + 3, clearOn: "manual", icon: "◎" });
  } else
    post(
      "table",
      "narr",
      null,
      tr(`林主任：「${c.text}」小周調整了鏡頭。`, `Dr. Lin: “${c.text}” Chou adjusts the camera.`),
    );
}

/* ---------- surgery flow ---------- */

function setWorking(on: boolean): void {
  for (const id of ["vs", "r2"]) {
    const e = findEnt(id);
    if (e) e.working = on;
  }
}

function endTimeout(): void {
  if (S.timeout.done) return;
  S.timeout.done = true;
  clearWait("to");
  S.draped = true;
  S.phase = "lap";
  S.lapAt = S.t;
  eventBanner(tr("劃刀", "Incision"), tr("建立氣腹，放入鏡頭", "Pneumoperitoneum up, scope in"));
  post(
    "table",
    "narr",
    null,
    S.atCamera
      ? tr("鋪單、建立氣腹，鏡頭放進去。", "Drapes on, pneumoperitoneum established, scope in.")
      : S.sterile
        ? tr(
            "鋪單、建立氣腹，鏡頭放進去。小周先扶著，你可以接過來。",
            "Drapes on, pneumoperitoneum established, scope in. Chou holds it for now; you can take over.",
          )
        : tr(
            "鋪單、建立氣腹，鏡頭放進去。小周先扶鏡頭。",
            "Drapes on, pneumoperitoneum established, scope in. Chou takes the camera for now.",
          ),
  );
}

function askQ(q: string): void {
  if (S.q) qMiss();
  if (S.qToR2) {
    post("table", "npc", "vs", tr("小周，", "Chou, ") + q);
    S.r2Answer = { t: S.t + 1, a: (QA[q] ?? "") + tr("。", ".") };
    S.qRes.push({ q, j: "skipped" });
    return;
  }
  S.q = q;
  S.qAt = S.t;
  ask("table", "vs", (S.sterile ? "" : tr("外面那個 clerk，", "You, the clerk out there: ")) + q, {
    id: "q",
    text: tr("林主任在等你回答", "Dr. Lin is waiting for your answer"),
    dlg: "table",
    clearOn: "manual",
  });
}

function qMiss(): void {
  if (!S.q) return;
  const q = S.q;
  S.q = null;
  clearWait("q");
  flag("vs");
  S.qRes.push({ q, j: "none" });
  S.trust--;
  S.mood.vs = (S.mood.vs ?? 0) + 1;
  post(
    "table",
    "npc",
    "vs",
    tr("問你話不回？", "I ask you something and you don’t answer? ") +
      (QA[q] ?? "") +
      tr("。回去查。", ". Go look it up."),
  );
  trustCheck();
}

function judged(j: Judge): void {
  if (j === "correct") S.trust++;
  else if (j === "wrong") S.trust--;
  trustCheck();
}

function trustCheck(): void {
  if (S.trust <= -2 && !S.qToR2) {
    S.qToR2 = true;
    post("table", "npc", "vs", tr("算了。接下來的問題小周回答。", "Forget it. Chou answers the rest."));
  }
  if (S.trust >= 2 && !S.offerSuture) {
    S.offerSuture = true;
    post("table", "npc", "vs", tr("不錯。等一下關傷口，皮你來縫。", "Not bad. When we close, you do the skin."));
  }
}

function resolveCount(text: string, by: string): void {
  if (S.count.resolved) return;
  S.count.resolved = true;
  S.count.by = by;
  clearWait("count");
  post("table", "narr", null, text);
  post("table", "npc", "scrub", tr("10 塊，數目對了。", "Ten. Count is correct."));
  S.phase = "close";
  S.closeAt = S.t;
  eventBanner(tr("關腹", "Closing"), tr("計數正確", "Count correct"));
}

function ending(): void {
  let code: string;
  let name: string;
  if (S.disclosed) {
    code = "D";
    name = tr("媽媽知道了", "Mom Found Out");
  } else if (!S.motherPost) {
    code = "E";
    name = tr("主任自己去講了", "Dr. Lin Told Her Himself");
  } else if (S.banned || S.contam >= 2) {
    code = "C";
    name = tr("阿珍記住你了", "Jen Remembers You");
  } else if (S.contam === 0 && S.cam.hit >= S.cam.miss + S.cam.wrong && (S.trust >= 2 || S.sutured)) {
    code = "A";
    name = tr("漂亮的一台刀", "A Beautiful Case");
  } else if (S.qToR2) {
    code = "B";
    name = tr("主任改問小周", "Dr. Lin Asked Chou Instead");
  } else {
    code = "B";
    name = tr("平安下刀", "Safely Off the Table");
  }
  const last = S.banned
    ? tr("明天去跟阿珍道歉。", "Go apologize to Jen tomorrow.")
    : S.sutured
      ? tr("皮縫得還可以，下次讓你多做一點。", "The skin closure was decent. Next time I’ll let you do more.")
      : S.qToR2
        ? tr("問題回去查清楚，下次我還會問。", "Look those questions up. I’ll ask again next time.")
        : S.cam.miss > S.cam.hit
          ? tr("鏡頭回去多練練。", "Go practice the camera.")
          : S.contam
            ? tr("無菌的東西，下次不要讓阿珍再講。", "Sterile technique: don’t make Jen tell you again next time.")
            : tr("今天鏡頭扶得不錯。", "You held the camera well today.");
  void playEnding(
    code,
    name,
    [
      {
        fn: () => {
          S.draped = false;
        },
        who: "anes",
        text: tr("拔管了，呼吸很好。推恢復室。", "Extubated, breathing well. Off to recovery."),
      },
      {
        fn: () => {
          S.tableAnim = { t0: performance.now() };
          leaveBy(ent("anes"), DOOR);
          const alive = sessionGuard();
          setTimeout(() => {
            if (alive()) leaveBy(ent("circ"), DOOR);
          }, 600);
        },
        text: tr(
          "曉婷被推往恢復室。房間的燈一盞一盞關掉。",
          "Hsiao-Ting is wheeled off to recovery. The lights in the room go off one by one.",
        ),
        wait: 3000,
      },
      {
        fn: () => walkEnt(ent("vs"), [3, 7]),
        who: "vs",
        text: tr("陳媽媽，我是林主任，我來跟您說明。", "Mrs. Chen, I’m Dr. Lin. Let me explain what happened."),
        wait: 3200,
      },
      {
        who: "mother",
        text: S.disclosed
          ? tr("……她懷孕的事，為什麼沒有人先告訴我？", "…She was pregnant? Why didn’t anyone tell me first?")
          : tr("醫生，她還好嗎？她會不會怎樣？", "Doctor, is she OK? Is she going to be all right?"),
      },
      { who: "vs", text: last },
    ],
    () =>
      tr(
        `污染 ${S.contam} 次，鏡頭 對 ${S.cam.hit}／錯 ${S.cam.wrong}／漏 ${S.cam.miss}，計數：${S.count.by ?? "未解決"}。`,
        `Contaminations: ${S.contam}. Camera: ${S.cam.hit} right / ${S.cam.wrong} wrong side / ${S.cam.miss} missed. Count: ${S.count.by ?? "unresolved"}.`,
      ),
  );
}

function monData(): MonitorData {
  const rel = S.lapAt != null ? S.t - S.lapAt + 11 : 0;
  if (S.phase === "lap" && rel >= 15 && rel < 19)
    return { hr: 128, sbp: 82, dbp: 45, spo2: 100, rr: 12, co2: 30, alarm: true };
  if (!S.asleep) return { hr: 118, sbp: 98, dbp: 60, spo2: 99, rr: 20 };
  if (S.phase === "post" || S.phase === "close") return { hr: 96, sbp: 112, dbp: 70, spo2: 100, rr: 12, co2: 36 };
  return { hr: 112, sbp: 96, dbp: 58, spo2: 100, rr: 12, co2: 34 };
}

function known(): string {
  const out: string[] = [];
  out.push(
    tr(
      `刷手：${S.scrubbed ? "有" : "沒有"}；目前無菌：${S.sterile ? "是" : "否"}；污染次數：${S.contam}${S.banned ? "；被請下台" : ""}`,
      `Scrubbed: ${S.scrubbed ? "yes" : "no"}; currently sterile: ${S.sterile ? "yes" : "no"}; contaminations: ${S.contam}${S.banned ? "; sent off the table" : ""}`,
    ),
  );
  const toAns =
    Object.entries(S.timeout.ans)
      .map(([k, v]) => k + "=" + v)
      .join(tr("、", ", ")) || tr("無", "none");
  out.push(
    tr(
      `Time-out：${S.timeout.withClerk ? "在場" : "不在場"}；回答：${toAns}`,
      `Time-out: ${S.timeout.withClerk ? "present" : "absent"}; answers: ${toAns}`,
    ),
  );
  out.push(
    tr(
      `扶鏡頭：正確 ${S.cam.hit}、看錯邊 ${S.cam.wrong}、漏掉 ${S.cam.miss}`,
      `Camera: correct ${S.cam.hit}, wrong side ${S.cam.wrong}, missed ${S.cam.miss}`,
    ),
  );
  out.push(
    tr(
      `主任提問與評判：${S.qRes.map((q) => q.q + "→" + q.j).join("；") || "無"}`,
      `Attending's questions and grades: ${S.qRes.map((q) => q.q + " -> " + q.j).join("; ") || "none"}`,
    ),
  );
  out.push(
    tr(
      `計數不符的處理：${S.count.tries.join("、") || "無"}；在鏡頭畫面上指認：${S.picks.join("、") || "無"}；${
        S.count.resolved ? "解決方式：" + (S.count.by ?? "") + "（紗布在" + S.gauze.where + "）" : "未解決"
      }`,
      `Handling of the count discrepancy: ${S.count.tries.join(", ") || "none"}; spots pointed out on the camera view: ${S.picks.join(", ") || "none"}; ${
        S.count.resolved
          ? "resolved by: " + (S.count.by ?? "") + " (sponge was in " + S.gauze.where + ")"
          : "unresolved"
      }`,
    ),
  );
  out.push(tr(`讀過急診病歷：${S.erRead ? "是" : "否"}`, `Read the ER chart: ${S.erRead ? "yes" : "no"}`));
  out.push(
    tr(
      `術前和曉婷談過：${S.revealed.size ? [...S.revealed].map((id) => FACTS[id]).join("；") : "沒有"}`,
      `Talked with Hsiao-Ting before surgery: ${S.revealed.size ? [...S.revealed].map((id) => FACTS[id]).join("; ") : "no"}`,
    ),
  );
  out.push(
    tr(
      `術後與陳媽媽對話：${S.motherPost} 次；是否透露懷孕或子宮外孕：${S.disclosed ? "是" : "否"}`,
      `Talked with Mrs. Chen after surgery: ${S.motherPost} time(s); disclosed the pregnancy or ectopic: ${S.disclosed ? "yes" : "no"}`,
    ),
  );
  return out.join("\n");
}

/* ---------- dialogs ---------- */

const PHASE_SUB: Record<Phase, string> = {
  preop: tr("曉婷還醒著", "Hsiao-Ting is still awake"),
  timeout: "Time-out",
  lap: tr("腹腔鏡中", "Laparoscopy underway"),
  count: tr("紗布少一塊", "A sponge is missing"),
  close: tr("關閉中", "Closing"),
  post: tr("手術結束", "Case over"),
};
const PHASE_HUD: Record<Phase, string> = {
  preop: tr("準備中", "Prepping"),
  timeout: "Time-out",
  lap: tr("腹腔鏡中", "Laparoscopy"),
  count: tr("計數不符", "Count off"),
  close: tr("關閉中", "Closing"),
  post: tr("結束", "Done"),
};
const JUDGE_LABEL: Record<Judge, string> = {
  correct: tr("答對", "right"),
  partial: tr("部分", "partial"),
  wrong: tr("答錯", "wrong"),
  none: tr("沒回答", "no answer"),
  skipped: tr("改問小周", "went to Chou"),
};

/** Count-discrepancy actions; the label doubles as the key in S.count.tries. */
const TRY_RECOUNT = tr("請佩佩重數紗布桶", "Ask Pei-Pei to recount the bucket");
const TRY_SCOPE = tr("用鏡頭再看一次腹腔", "Recheck the abdomen with the camera");
const TRY_XRAY = tr("建議照 X 光", "Suggest an X-ray");
const TRY_CLOSE = tr("先關，等下再找", "Close now, look later");

function suggestXray(): void {
  post(
    "table",
    "npc",
    "vs",
    tr(
      "好，佩佩叫 portable。照的同時繼續用鏡頭找。",
      "OK. Pei-Pei, call for a portable. Keep searching with the camera while they shoot it.",
    ),
  );
  S.xrayAt = S.t + 6;
  spend(1);
}

const TIMEOUT: TimeoutItem[] = [
  {
    k: tr("側別", "Side"),
    opts: [
      { v: tr("右側", "Right"), ok: true, reply: tr("右側，確認。", "Right side, confirmed.") },
      {
        v: tr("左側", "Left"),
        ok: false,
        reply: tr(
          "左邊？TVS 寫右側附屬器，你自己在急診報告的。",
          "Left? The TVS says right adnexa. You presented it yourself in the ER.",
        ),
      },
      {
        v: tr("不確定", "Not sure"),
        ok: false,
        reply: tr("不確定就不要站在這裡。右側。", "If you’re not sure, don’t stand here. Right side."),
      },
    ],
  },
  {
    k: tr("備血", "Blood"),
    opts: [
      {
        v: tr("已備 2U，第一袋在房間", "2U ready, first unit in the room"),
        ok: true,
        reply: tr("對，2U 已經在冰箱。", "Right, 2U already in the fridge."),
      },
      {
        v: tr("沒有備血", "None ordered"),
        ok: false,
        reply: tr("有備，你沒追。", "It’s ordered. You didn’t follow up."),
      },
      {
        v: tr("不知道", "Don’t know"),
        ok: false,
        reply: tr("去問佩佩啊。2U 已經備好了。", "Then ask Pei-Pei. 2U are already ready."),
      },
    ],
  },
  {
    k: tr("過敏", "Allergies"),
    opts: [
      { v: tr("無藥物過敏", "NKDA"), ok: true, reply: tr("無過敏，確認。", "No allergies, confirmed.") },
      {
        v: tr("Penicillin 過敏", "Penicillin"),
        ok: false,
        reply: tr("哪裡寫的？她沒有過敏。", "Where does it say that? She has no allergies."),
      },
      {
        v: tr("不知道", "Don’t know"),
        ok: false,
        reply: tr("急診紀錄有寫，沒有過敏。", "It’s in the ER record: no allergies."),
      },
    ],
  },
];

function tableChoices(): ChoiceItem[] {
  if (S.phase === "timeout" && S.timeout.withClerk)
    return timeoutChoices(TIMEOUT, S.timeout.ans, {
      ch: "table",
      okWho: "circ",
      badWho: "vs",
      onWrong: () => {
        S.mood.vs = (S.mood.vs ?? 0) + 1;
      },
      onDone: endTimeout,
    });
  if (S.phase === "lap") {
    if (!S.sterile) return [];
    if (!S.atCamera)
      return [
        C(tr("從小周手上接過鏡頭", "Take the camera from Chou"), () => {
          S.atCamera = true;
          clearWait("cam0");
          flag("vs");
          me("table", tr("（接過鏡頭）", "(Takes the camera)"));
          post(
            "table",
            "npc",
            "r2",
            tr("給你，穩一點，主任說什麼就做什麼。", "Here. Keep it steady, and do whatever Dr. Lin says."),
          );
          openLap();
        }),
      ];
    return [C(tr("看鏡頭畫面", "View the scope"), openLap)];
  }
  if (S.phase === "close" && S.offerSuture && !S.sutured && S.sterile) {
    return [
      tr("關傷口", "Closing"),
      C(tr("縫皮（3 分鐘）", "Close the skin (3 min)"), () => {
        S.sutured = true;
        clearWait("sut");
        flag("vs");
        me("table", tr("（縫皮）", "(Sutures the skin)"));
        post(
          "table",
          "narr",
          null,
          tr(
            "你在主任盯著下，縫好三個 trocar 傷口的皮。",
            "Under Dr. Lin’s eye, you close the skin of all three trocar sites.",
          ),
        );
        post(
          "table",
          "npc",
          "vs",
          S.cam.miss > S.cam.hit
            ? tr("針距還可以。鏡頭要是也扶這麼穩就好了。", "Spacing is fine. If only you held the camera that steady.")
            : tr("針距還可以。", "Spacing is fine."),
        );
        spend(3);
      }),
    ];
  }
  if (S.phase === "count" && !S.count.resolved && S.atCamera && S.count.tries.includes(TRY_SCOPE)) {
    return [
      tr("紗布少一塊", "Missing sponge"),
      C(tr("看鏡頭畫面", "View the scope"), openLap),
      ...(!S.count.tries.includes(TRY_XRAY)
        ? [
            C(TRY_XRAY, () => {
              S.count.tries.push(TRY_XRAY);
              me("table", tr("（建議照 X 光）", "(Suggests an X-ray)"));
              suggestXray();
            }),
          ]
        : []),
    ];
  }
  if (S.phase === "count" && !S.count.resolved) {
    const attempt = (label: string, fn: () => void) =>
      C(
        label,
        () => {
          if (S.count.tries.includes(label)) return;
          S.count.tries.push(label);
          me("table", tr("（" + label + "）", "(" + label + ")"));
          fn();
        },
        S.count.tries.includes(label),
      );
    return [
      tr("紗布少一塊", "Missing sponge"),
      attempt(TRY_RECOUNT, () => {
        post(
          "table",
          "npc",
          "circ",
          tr(
            "我重數了，紗布桶裡 9 塊，鋪單下面也找過，沒有。",
            "I recounted: 9 in the bucket. I checked under the drapes too. Nothing.",
          ),
        );
        spend(2);
      }),
      attempt(TRY_SCOPE, () => {
        if (!S.atCamera && S.sterile) {
          S.atCamera = true;
          post("table", "npc", "r2", tr("鏡頭給你。", "Camera’s yours."));
        }
        if (S.atCamera) {
          post(
            "table",
            "npc",
            "vs",
            tr("你找。看到可疑的，點給我看。", "You look. If you see anything suspicious, point it out."),
          );
          addWait({
            id: "findg",
            text: tr("用鏡頭找紗布，看到可疑的就點它", "Find the sponge with the camera; click anything suspicious"),
            clearOn: "manual",
            icon: "◎",
          });
          openLap();
        } else {
          post(
            "table",
            "npc",
            "r2",
            tr("我來看……你在下面幫我盯螢幕。", "I’ll look… you watch the screen for me from down there."),
          );
          S.r2Search = S.t + 5;
        }
      }),
      attempt(TRY_XRAY, suggestXray),
      attempt(TRY_CLOSE, () => {
        annoy(
          "vs",
          tr(
            ["不行！數目不對絕對不能關。", "我說過了，不能關！"],
            ["No! We never close with the count off.", "I already said it: we are not closing!"],
          ),
          "table",
        );
        spend(1);
      }),
    ];
  }
  return [];
}

const dialogs: Level["dialogs"] = {
  erchart: {
    title: tr("曉婷的急診病歷", "Hsiao-Ting’s ER chart"),
    sub: tr("護理紀錄電腦", "Nursing workstation"),
    ch: "erchart",
    doc: doc(() => {
      S.erRead = true;
      return [
        [tr("基本資料", "Patient"), tr("林曉婷，19 歲，G1P0。", "Lin Hsiao-Ting, 19, G1P0.")],
        [
          tr("急診診斷", "ER diagnosis"),
          tr("右側子宮外孕破裂，腹內出血。", "Ruptured right ectopic pregnancy, hemoperitoneum."),
        ],
        [
          tr("檢驗", "Labs"),
          tr(
            "UPT (+)，β-hCG 2,850 mIU/mL，Hb 10.4 g/dL，血型 O 型 Rh(+)。",
            "UPT (+), β-hCG 2,850 mIU/mL, Hb 10.4 g/dL, blood type O Rh(+).",
          ),
        ],
        [
          tr("影像", "Imaging"),
          tr(
            "陰道超音波：子宮腔內無妊娠囊；右側附屬器 3.2 cm 複雜性腫塊；後穹窿中到大量積液。",
            "Transvaginal US: no intrauterine gestational sac; 3.2 cm complex right adnexal mass; moderate to large free fluid in the posterior cul-de-sac.",
          ),
        ],
        [tr("最後進食", "Last meal"), tr("18:00，一碗麵。之後只喝水。", "18:00, a bowl of noodles. Only water since.")],
        [tr("過敏", "Allergies"), tr("無藥物過敏。", "No known drug allergies.")],
        [
          tr("備血", "Blood"),
          tr("2U PRBC 已備，第一袋在房間冰箱。", "2U PRBC crossmatched; first unit in the OR fridge."),
        ],
        [
          tr("同意書", "Consent"),
          tr("手術與麻醉同意書，病人本人簽署。", "Surgical and anesthesia consents, signed by the patient herself."),
        ],
        [
          tr("護理紀錄備註", "Nursing note"),
          tr(
            "病人要求：懷孕相關病情，先不要告知家屬。",
            "Patient request: do not tell family anything about the pregnancy for now.",
          ),
        ],
      ];
    }),
  },
  scrub: scrubSinkDialog(),
  scrubn: {
    title: tr("阿珍", "Jen"),
    sub: tr("刷手護理師", "Scrub nurse"),
    pics: ["scrub"],
    ch: "scrubn",
    talk: true,
    ph: tr("對阿珍說…", "Say to Jen…"),
    choices() {
      return [gownChoice(() => S.phase === "close" || S.phase === "post")];
    },
    async onTalk(text) {
      await chat(text, {
        ch: "scrubn",
        think: "scrub",
        speakers: { scrub: tr("阿珍", "Jen") },
        cost: 1,
        body: tr(
          `你在醫學教育遊戲中扮演手術室刷手護理師「阿珍」，四十歲，對無菌技術極度嚴格，嘴巴兇但會教。玩家是見習醫學生（clerk）。
現況：${clock()}；手術階段 ${S.phase}；clerk ${S.sterile ? "已經穿好手術衣" : "還沒穿手術衣"}${S.scrubbed ? "、刷過手" : "、還沒刷手"}；污染過 ${S.contam} 次${S.banned ? "，已經被你請下台" : ""}。${S.phase === "count" ? "現在紗布少一塊，開了 10 塊只數到 9 塊，你堅持不能關。" : ""}
不要替 clerk 做決定，但可以提醒無菌規則。`,
          `In a medical education game you play "Jen," the OR scrub nurse: forty, extremely strict about sterile technique, sharp-tongued but she teaches. The player is a clerk (medical student on clerkship).
Current state: ${clock()}; surgical phase ${S.phase}; the clerk ${S.sterile ? "is already gowned" : "is not gowned yet"}${S.scrubbed ? ", has scrubbed" : ", has not scrubbed yet"}; contaminated ${S.contam} time(s)${S.banned ? ", and you have already sent them off the table" : ""}.${S.phase === "count" ? " A sponge is missing right now: 10 were opened and only 9 are counted, and you insist the case cannot be closed." : ""}
Do not make decisions for the clerk, but you may remind them of the sterile rules.`,
        ),
      });
    },
  },
  table: {
    title: tr("手術台", "OR table"),
    sub: () => PHASE_SUB[S.phase],
    pics: () => (S.asleep ? ["vs", "r2", "anes", "scrub", "circ"] : ["xiaoting", "vs", "r2"]),
    ch: "table",
    talk: true,
    ph: () =>
      S.asleep ? tr("對手術台旁的人說…", "Say to the team at the table…") : tr("對曉婷說…", "Say to Hsiao-Ting…"),
    choices: tableChoices,
    async onTalk(text) {
      if (!S.asleep) {
        await chat(text, {
          ch: "table",
          think: "xiaoting",
          speakers: tr(
            { xiaoting: "曉婷", vs: "林主任", r2: "小周", anes: "陳醫師（麻醉科）" },
            { xiaoting: "Hsiao-Ting", vs: "Dr. Lin", r2: "Chou", anes: "Dr. Chen (anesthesia)" },
          ),
          cost: 1,
          body: tr(
            `你在醫學教育遊戲中扮演手術台旁的人：還醒著、馬上要麻醉的「林曉婷」（19 歲，右側子宮外孕破裂）為主，林主任、小周、麻醉科陳醫師偶爾插一句。玩家是見習醫學生（clerk）。
曉婷很怕，手在發抖。她最想講的事（被問到她的感覺、或 clerk 安撫她時會說）：
- (secret) 拜託不要跟媽媽說我懷孕、有男朋友的事
- (fertility) 我以後還能生小孩嗎？
- (scared) 我會不會醒不過來？
林主任：若 clerk 對生育問題講錯（例如說一定不能生），他會簡短修正（另一側輸卵管正常的話還是有機會）。陳醫師：只在 clerk 談到麻醉、禁食時插話。不要講太多醫學細節。`,
            `In a medical education game you play the people at the operating table: mainly "Lin Hsiao-Ting" (19, ruptured right ectopic pregnancy), still awake and about to go under; Dr. Lin, Chou and Dr. Chen from anesthesia occasionally chime in. The player is a clerk (medical student on clerkship).
Hsiao-Ting is very scared; her hands are shaking. The things she most wants to say (she says them when asked how she feels, or when the clerk comforts her):
- (secret) Please don't tell my mom I'm pregnant or that I have a boyfriend
- (fertility) Will I still be able to have kids?
- (scared) What if I don't wake up?
Dr. Lin: if the clerk gets the fertility question wrong (e.g. says she definitely can't have children), he corrects it briefly (if the other tube is normal, she still has a chance). Dr. Chen: chimes in only when the clerk talks about anesthesia or NPO status. Don't go into much medical detail.`,
          ),
          extra: `"revealed":[]`,
          after(r) {
            for (const id of strList(r.revealed)) if (isFact(id)) S.revealed.add(id);
          },
        });
        return;
      }
      const q = S.q;
      await chat(text, {
        ch: "table",
        think: "vs",
        tier: "default",
        speakers: tr(
          {
            vs: "林主任（主刀）",
            r2: "小周（R2，第一助手）",
            anes: "陳醫師（麻醉科）",
            scrub: "阿珍（刷手護理師）",
            circ: "佩佩（流動護理師）",
          },
          {
            vs: "Dr. Lin (operating surgeon)",
            r2: "Chou (R2, first assistant)",
            anes: "Dr. Chen (anesthesia)",
            scrub: "Jen (scrub nurse)",
            circ: "Pei-Pei (circulating nurse)",
          },
        ),
        cost: 1,
        body: tr(
          `你在醫學教育遊戲中同時扮演手術室裡的五個人。正在做緊急腹腔鏡右側輸卵管切除（子宮外孕破裂，腹內出血約 1,200 mL）。玩家是見習醫學生（clerk）${S.atCamera ? "，正在扶鏡頭" : S.sterile ? "，穿著手術衣站在台邊" : "，沒有刷手，站在台下"}。
【每個人知道和在意的事】
林主任：開刀時話很少，會考學生。不直接說答案。
小周：幫忙、會偷偷提示 clerk（例如「病人的右邊在畫面左邊」），但主任在考試時不會替他回答。
陳醫師：只管血行動力學、輸血、麻醉深度。${S.phase === "lap" ? "剛剛血壓掉到 82/45，已掛血。" : ""}
阿珍：只管器械、紗布數目與無菌。${S.phase === "count" ? "現在紗布少一塊，堅持不能關。" : ""}
佩佩：只管拿東西、打電話、掛血、記錄。
${q ? `【待回答的問題】林主任剛剛問 clerk：「${q}」參考答案：${QA[q] ?? ""}。用 judge 評判 clerk 這句話對這題的回答：correct、partial、wrong；如果這句話不是在回答這題，judge 用 none。只能由林主任回應評判，答對就補一句延伸，答錯就直接說正確答案並唸一句。` : "目前沒有待回答的問題，judge 用 none。"}
回應要簡短，符合手術現場的節奏。`,
          `In a medical education game you play five people in the OR at once. An emergency laparoscopic right salpingectomy is underway (ruptured ectopic pregnancy, hemoperitoneum of about 1,200 mL). The player is a clerk (medical student on clerkship)${S.atCamera ? ", currently holding the camera" : S.sterile ? ", gowned and standing at the table" : ", not scrubbed, standing off the table"}.
[WHAT EACH PERSON KNOWS AND CARES ABOUT]
Dr. Lin: says very little while operating and quizzes the student. Never gives the answer outright.
Chou: helps and quietly hints to the clerk (e.g. "the patient's right is on the left of the screen"), but will not answer for the clerk while Dr. Lin is quizzing.
Dr. Chen: only concerned with hemodynamics, transfusion and depth of anesthesia.${S.phase === "lap" ? " BP just dropped to 82/45; blood is already hung." : ""}
Jen: only concerned with instruments, the sponge count and sterility.${S.phase === "count" ? " A sponge is missing right now; she insists the case cannot be closed." : ""}
Pei-Pei: only fetches things, makes calls, hangs blood and charts.
${q ? `[PENDING QUESTION] Dr. Lin just asked the clerk: "${q}" Reference answer: ${QA[q] ?? ""}. Use judge to grade how the clerk's line answers this question: correct, partial or wrong; if the line is not an answer to this question, set judge to none. Only Dr. Lin responds to the grading: if correct, he adds one line taking it further; if wrong, he states the correct answer directly and gives a one-line rebuke.` : "There is no pending question right now; set judge to none."}
Keep replies short, in keeping with the pace of an operation.`,
        ),
        extra: `"judge":"none"`,
        after(r) {
          if (q && isGrade(r.judge) && S.q === q) {
            S.qRes.push({ q, j: r.judge });
            S.q = null;
            clearWait("q");
            if (r.judge === "wrong") S.mood.vs = (S.mood.vs ?? 0) + 1;
            judged(r.judge);
          }
        },
      });
    },
  },
  anes: {
    title: tr("陳醫師", "Dr. Chen"),
    sub: tr("麻醉科主治", "Anesthesiology attending"),
    pics: ["anes"],
    ch: "anes",
    talk: true,
    ph: tr("對陳醫師說…", "Say to Dr. Chen…"),
    async onTalk(text) {
      await chat(text, {
        ch: "anes",
        think: "anes",
        speakers: { anes: tr("陳醫師", "Dr. Chen") },
        cost: 1,
        body: tr(
          `你在醫學教育遊戲中扮演麻醉科主治「陳醫師」，冷靜、精準、不喜歡廢話。玩家是婦產科見習醫學生（clerk）。
病人林曉婷 19 歲，子宮外孕破裂、腹內出血，急診 HR 120 以上、血壓 80 幾。真相：最後一次進食是晚上六點一碗麵；無過敏；已備 2U。
你在術前問 clerk 最後進食時間；clerk 答對就說「好，當 full stomach 處理，RSI」，答錯或不知道就說「不知道就說不知道，不要猜」。術中若 clerk 來問，你可以簡短說明血行動力學。
現況：${clock()}；階段 ${S.phase}。`,
          `In a medical education game you play "Dr. Chen," the anesthesiology attending: calm, precise, no patience for idle talk. The player is an OB/GYN clerk (medical student on clerkship).
The patient, Lin Hsiao-Ting, 19, has a ruptured ectopic pregnancy with hemoperitoneum; in the ER her HR was over 120 and her BP in the 80s. The truth: her last meal was a bowl of noodles at 18:00; no allergies; 2 units are ready.
Before surgery you ask the clerk when she last ate; if the clerk answers correctly, say "OK, we treat her as a full stomach. RSI." If wrong or they don't know, say "If you don't know, say you don't know. Don't guess." If the clerk asks you during the case, you can briefly explain her hemodynamics.
Current state: ${clock()}; phase ${S.phase}.`,
        ),
      });
    },
  },
  circ: {
    title: tr("佩佩", "Pei-Pei"),
    sub: tr("流動護理師", "Circulating nurse"),
    pics: ["circ"],
    ch: "circ",
    talk: true,
    ph: tr("對佩佩說…", "Say to Pei-Pei…"),
    async onTalk(text) {
      await chat(text, {
        ch: "circ",
        think: "circ",
        speakers: { circ: tr("佩佩", "Pei-Pei") },
        cost: 1,
        body: tr(
          `你在醫學教育遊戲中扮演手術室流動護理師「佩佩」，二十多歲，跑來跑去處理所有雜事：拿器械、打電話、掛血、記錄、數紗布。玩家是見習醫學生（clerk）。
現況：${clock()}；階段 ${S.phase}；2U PRBC 已備。${S.phase === "count" ? "紗布開 10 塊，桶裡只有 9 塊，鋪單下面你找過沒有。" : ""}
可以回答 clerk 關於流程、病歷、備血的問題。不要替他做醫療決定。`,
          `In a medical education game you play "Pei-Pei," the OR circulating nurse: in her twenties, running around handling every odd job: fetching instruments, making calls, hanging blood, charting, counting sponges. The player is a clerk (medical student on clerkship).
Current state: ${clock()}; phase ${S.phase}; 2U PRBC ready.${S.phase === "count" ? " 10 sponges were opened, only 9 are in the bucket, and you have already searched under the drapes: nothing." : ""}
You can answer the clerk's questions about workflow, the chart and blood prep. Do not make medical decisions for them.`,
        ),
      });
    },
  },
  mother: {
    title: tr("陳媽媽", "Mrs. Chen"),
    sub: tr("家屬等候區", "Family waiting area"),
    pics: ["mother"],
    ch: "mother",
    talk: true,
    ph: tr("對陳媽媽說…", "Say to Mrs. Chen…"),
    async onTalk(text) {
      if (S.phase === "post") S.motherPost++;
      else S.motherPre++;
      await chat(text, {
        ch: "mother",
        think: "mother",
        speakers: { mother: tr("陳媽媽", "Mrs. Chen") },
        tier: "default",
        body: tr(
          `你在醫學教育遊戲中扮演「陳媽媽」，52 歲，女兒林曉婷（19 歲）正在開刀。玩家是婦產科見習醫學生（clerk），${S.sterile ? "穿著手術衣" : "戴著手術帽口罩"}走出來。
陳媽媽很焦慮，一直以為是盲腸炎，不懂為什麼是婦產科開刀。她會追問「到底是什麼病？」「拿掉了什麼？」「是不是懷孕？」「她以後還能生嗎？」。她完全不知道女兒有男朋友。
真相：子宮外孕破裂，切除右側輸卵管，手術${S.phase === "post" ? "已經結束、很順利" : "還在進行"}。病人是成年人，術前拜託不要讓媽媽知道懷孕的事；clerk ${S.revealed.has("secret") || S.erRead ? "知道" : "不一定知道"}這件事。
如果 clerk 明確說出懷孕、子宮外孕或懷孕相關的事，disclosed 設為 true，並讓媽媽有強烈情緒反應（震驚、質問男朋友是誰）。如果 clerk 說主任會來詳細說明、或說要等女兒醒來，媽媽會不太滿意但接受。
現況：${clock()}；手術階段 ${S.phase}。`,
          `In a medical education game you play "Mrs. Chen," 52; her daughter Lin Hsiao-Ting (19) is in surgery. The player is an OB/GYN clerk (medical student on clerkship) who comes out ${S.sterile ? "in a surgical gown" : "in a surgical cap and mask"}.
Mrs. Chen is very anxious. She has assumed all along it was appendicitis and doesn't understand why OB/GYN is operating. She keeps pressing: "What is it, really?" "What did you take out?" "Is she pregnant?" "Will she still be able to have children?" She has no idea her daughter has a boyfriend.
The truth: ruptured ectopic pregnancy; the right fallopian tube was removed; the surgery ${S.phase === "post" ? "is over and went smoothly" : "is still going"}. The patient is an adult and asked before surgery that her mother not be told about the pregnancy; the clerk ${S.revealed.has("secret") || S.erRead ? "knows" : "may or may not know"} this.
If the clerk explicitly mentions the pregnancy, the ectopic pregnancy or anything pregnancy-related, set disclosed to true and have Mrs. Chen react with strong emotion (shock, demanding to know who the boyfriend is). If the clerk says Dr. Lin will come and explain in detail, or that they should wait until her daughter wakes up, she is not very satisfied but accepts it.
Current state: ${clock()}; surgical phase ${S.phase}.`,
        ),
        extra: `"disclosed":false`,
        after(r) {
          if (r.disclosed) S.disclosed = true;
          if (S.phase === "post" && S.motherPost >= 1 && !S.endQueued) {
            S.endQueued = true;
            const alive = sessionGuard();
            setTimeout(() => {
              if (alive()) ending();
            }, 2500);
          }
        },
      });
    },
  },
};

/* ---------- level ---------- */

export const ORL: Level = {
  id: "or",
  no: tr("五", "5"),
  short: tr("第五關　刀房", "Level 5 · OR"),
  title: tr("刀房：第二關續", "OR: Level 2, Continued"),
  place: tr("手術室 5 房", "OR 5"),
  blurb: tr(
    "第二關的曉婷推進刀房了。主任叫你刷手進來扶鏡頭。",
    "Hsiao-Ting from Level 2 is wheeled into the OR. Dr. Lin tells you to scrub in and hold the camera.",
  ),
  cast: ["xiaoting", "mother", "vs", "r2", "anes", "scrub", "circ"],
  intro: tr(
    [
      "週五晚上 23:10。第二關急診的曉婷確診右側子宮外孕破裂，被推進手術室 5 房做緊急腹腔鏡。",
      "林主任跟小周已經穿好手術衣在房間裡，主任叫你：「刷完手進來扶鏡頭。」陳媽媽一個人坐在外面的家屬等候區。",
      "刀房的規矩：刷手之後手不能亂碰；穿好手術衣、戴好手套之後，就不能碰任何不是無菌的東西，也不能走出房間。",
    ],
    [
      "Friday night, 23:10. Hsiao-Ting, from the ER in Level 2, has been diagnosed with a ruptured right ectopic pregnancy and wheeled into OR 5 for emergency laparoscopy.",
      "Dr. Lin and Chou are already gowned in the room. Dr. Lin tells you: “Scrub and come hold the camera.” Mrs. Chen sits alone in the family waiting area outside.",
      "OR rules: once you scrub, your hands touch nothing. Once you are gowned and gloved, you can’t touch anything non-sterile, and you can’t leave the room.",
    ],
  ),
  start: 23 * 60 + 10,
  maxT: 80,
  night: true,
  idleSec: () => (S.live && (S.phase === "lap" || S.phase === "count") ? 6 : 10),
  map: [
    "####################",
    "#fff#sss#oooooooooo#",
    "#fff#sss#oooooooooo#",
    "#fff#sss#oooooooooo#",
    "#fff#sss#oooooooooo#",
    "#fff#ssssoooooooooo#",
    "#ffffssssoooooooooo#",
    "#fff#sss#oooooooooo#",
    "#fff#sss#oooooooooo#",
    "#fff#sss#oooooooooo#",
    "#fff#sss#oooooooooo#",
    "####################",
  ],
  zones: {
    f: ["#efe4d2", "#dccfb8", "#e6d9c4"],
    s: ["#d8e6f0", "#c2d5e3", "#cfdeea"],
    o: ["#d3e6df", "#bdd6cc", "#c8ded5"],
  },
  windows: [],
  exits: [DOOR, [2, 11]],
  chName: {
    table: tr("手術台", "OR table"),
    scrubn: tr("阿珍", "Jen"),
    circ: tr("佩佩", "Pei-Pei"),
    anes: tr("陳醫師", "Dr. Chen"),
    mother: tr("陳媽媽", "Mrs. Chen"),
    scrub: tr("刷手台", "Scrub sink"),
    sys: tr("刀房", "OR"),
  },
  objects: [
    { id: "vend", type: "vending", x: 1, y: 1 },
    { id: "fchairs", type: "chairs", x: 1, y: 8, w: 3 },
    { id: "fplant", type: "plant", x: 3, y: 1 },
    { id: "sink1", type: "scrubsink", x: 5, y: 1, label: tr("刷手台", "Scrub sink"), act: "scrub" },
    { id: "sink2", type: "scrubsink", x: 6, y: 1, label: tr("刷手台", "Scrub sink"), act: "scrub" },
    { id: "sink3", type: "scrubsink", x: 7, y: 1, label: tr("刷手台", "Scrub sink"), act: "scrub" },
    { id: "gowns", type: "shelf2", x: 7, y: 10 },
    { id: "table", type: "ortable", x: 13, y: 4, h: 3, label: tr("手術台", "OR table") },
    { id: "anesth", type: "anesth", x: 12, y: 1, label: tr("麻醉機", "Anesthesia machine") },
    { id: "ormon", type: "bp", x: 14, y: 1, label: tr("麻醉監視器", "Anesthesia monitor") },
    { id: "tower", type: "tower", x: 16, y: 3, h: 2, label: tr("腹腔鏡主機", "Lap tower") },
    { id: "suction", type: "suction", x: 10, y: 2 },
    { id: "esu", type: "esu", x: 10, y: 3 },
    { id: "mayo", type: "mayo", x: 14, y: 7, label: tr("Mayo 器械台", "Mayo stand"), act: "scrubn" },
    { id: "back", type: "backtable", x: 16, y: 8, w: 2, label: tr("器械桌", "Back table"), act: "scrubn" },
    { id: "kick", type: "kick", x: 11, y: 7, label: tr("紗布桶（kick bucket）", "Kick bucket") },
    { id: "board", type: "whiteboard", x: 18, y: 1, label: tr("計數白板", "Count board") },
    { id: "desk", type: "desk", x: 9, y: 10, w: 2 },
    { id: "erchart", type: "chart", x: 10, y: 10, label: tr("曉婷的急診病歷", "Hsiao-Ting’s ER chart") },
  ],
  labels: {
    table: tr("手術台", "OR table"),
    scrubn: tr("阿珍", "Jen"),
    circ: tr("佩佩", "Pei-Pei"),
    anes: tr("陳醫師", "Dr. Chen"),
    mother: tr("陳媽媽", "Mrs. Chen"),
    scrub: tr("刷手台", "Scrub sink"),
    erchart: tr("曉婷的急診病歷", "Hsiao-Ting’s ER chart"),
  },
  help: {
    scrub: tr("刷手 3 分鐘", "Scrub for 3 minutes"),
    table: tr("術前跟曉婷說話、time-out、扶鏡頭、計數", "Talk to Hsiao-Ting pre-op, time-out, camera, count"),
    anesth: tr("陳醫師的機器，別碰", "Dr. Chen’s machine. Hands off"),
    ormon: tr("看生命徵象（會打開大螢幕）", "Check vitals (opens the big screen)"),
    tower: tr("腹腔鏡畫面的主機", "The laparoscope’s video tower"),
    scrubn: tr("刷手護理師：幫你穿手術衣、戴手套", "Scrub nurse: gowns and gloves you"),
    kick: tr("用過的紗布", "Used sponges"),
    board: tr("紗布與器械計數", "Sponge and instrument count"),
    erchart: tr("急診的檢驗、進食時間、過敏、備血", "ER labs, last meal, allergies, blood"),
    circ: tr("流動護理師", "Circulating nurse"),
    anes: tr("麻醉科主治", "Anesthesiology attending"),
    mother: tr("在家屬等候區等女兒", "Waiting for her daughter in the family area"),
  },
  helpWarn: ["anesth", "ormon", "tower", "kick", "board", "erchart"],
  helpRules: tr(
    [
      "刷手之後：手不能碰任何東西，碰了要重刷。",
      "穿上手術衣之後：只能碰手術台和器械台（阿珍那邊）。碰到紅框的東西，或走出刀房，就算污染。",
      "要先看病歷的話，趁刷手之前看。",
      "扶鏡頭時：病人的右邊在畫面左邊。",
    ],
    [
      "After scrubbing: your hands touch nothing. Touch something and you rescrub.",
      "After gowning: touch only the OR table and the instrument tables (Jen’s side). Touching anything outlined in red, or walking out of the OR, is a contamination.",
      "If you want to read the chart, do it before you scrub.",
      "On the camera: the patient’s right is on the left of the screen.",
    ],
  ),
  player: [6, 6],
  ents: () => [
    mkEnt({ k: "vs_or", id: "vs", x: 12, y: 5, act: "table", face: "right" }),
    mkEnt({ k: "r2_or", id: "r2", x: 14, y: 4, act: "table", face: "left" }),
    mkEnt({ k: "anes", id: "anes", x: 13, y: 3, act: "anes", face: "down" }),
    mkEnt({ k: "scrub", id: "scrub", x: 15, y: 7, act: "scrubn", face: "left" }),
    mkEnt({ k: "circ", id: "circ", x: 10, y: 8, act: "circ" }),
    mkEnt({ k: "mother", id: "mother", x: 2, y: 8, act: "mother" }),
  ],
  init: (): Vars => ({
    trust: 0,
    qToR2: false,
    offerSuture: false,
    sutured: false,
    step: -1,
    phase: "preop",
    scrubbed: false,
    sterile: false,
    banned: false,
    contam: 0,
    atCamera: false,
    asleep: false,
    draped: false,
    salpDone: false,
    timeout: { done: false, withClerk: false, ans: {} },
    cmd: null,
    cam: { hit: 0, miss: 0, wrong: 0 },
    q: null,
    qAt: null,
    qRes: [],
    count: { at: null, resolved: false, tries: [], by: null },
    postAt: null,
    motherPost: 0,
    motherPre: 0,
    disclosed: false,
    revealed: new Set(),
    anesAsked: false,
    lap: newLap(150, 70),
    tableAnim: null,
    tableGone: false,
    warnedTable: "",
    gauze: GAUZE[Math.floor(Math.random() * GAUZE.length)] as Gauze,
    picks: [],
    xrayAt: null,
    xrayKnown: false,
    lastMissAt: -9,
    lapAt: null,
    r2Answer: null,
    r2Search: null,
    closeAt: null,
    erRead: false,
    endQueued: false,
  }),
  reset: scope.reset,
  dialogs,
  zooms: { table: () => (S.atCamera && (S.phase === "lap" || S.phase === "count") ? lapCfg() : null) },
  quick: {
    anesth: () => note(tr("麻醉機。陳醫師瞪了你一眼。", "The anesthesia machine. Dr. Chen shoots you a look.")),
    ormon: () => openMonitor(tr("麻醉監視器", "Anesthesia monitor"), () => ({ ...monData(), nibpT: clock() })),
    tower() {
      if (S.atCamera && (S.phase === "lap" || S.phase === "count")) openLap();
      else
        note(
          S.phase === "lap"
            ? tr("腹腔鏡畫面：一片血紅。小周扶著鏡頭。", "The scope view: a field of red. Chou is holding the camera.")
            : tr("腹腔鏡主機。", "The laparoscopy tower."),
        );
    },
    kick: () =>
      note(
        S.phase === "count"
          ? tr("紗布桶裡攤開的紗布：9 塊。", "Sponges laid out from the kick bucket: 9.")
          : tr(
              "紗布桶，用過的紗布會丟在這裡讓流動護理師數。",
              "The kick bucket. Used sponges go here for the circulator to count.",
            ),
      ),
    board: () =>
      note(
        S.phase === "count"
          ? tr("白板：紗布 開 10／數 9。針 3／3。", "Board: sponges opened 10 / counted 9. Needles 3/3.")
          : tr("白板：紗布 10、針 3、Trocar 3。", "Board: sponges 10, needles 3, trocars 3."),
      ),
  },

  clerkSprite: () => (S.sterile ? "clerk_gown" : "clerk_or"),
  beforeInteract(id) {
    guardTouch(id, NONSTERILE);
    if (id === "table" && (S.phase === "lap" || S.phase === "count") && !S.sterile && S.warnedTable !== S.phase) {
      S.warnedTable = S.phase;
      post(
        "scrubn",
        "npc",
        "scrub",
        tr(
          "沒刷手、沒穿手術衣不要靠近手術台！要講話站遠一點。",
          "Not scrubbed, not gowned: stay away from the table! If you want to talk, stand back.",
        ),
      );
    }
    return true;
  },
  onStep(x) {
    if (S.atCamera) {
      S.atCamera = false;
      if (S.cmd)
        annoy(
          "vs",
          tr(
            ["鏡頭不要放了就走！", "你又放掉鏡頭？", "……小周，你扶。"],
            ["Don’t just drop the camera and walk off!", "You let go of the camera again?", "…Chou, you take it."],
          ),
          "table",
        );
      else post("table", "narr", null, tr("你離開了鏡頭，小周接過去扶。", "You leave the camera; Chou takes it over."));
    }
    if (S.sterile && x < 9) contaminate(tr("你穿著手術衣走出刀房了。", "You walked out of the OR in your gown."));
  },
  onStart: () =>
    eventBanner(
      tr("手術室 5 房", "OR 5"),
      tr("右側子宮外孕破裂・緊急腹腔鏡", "Ruptured right ectopic · emergency laparoscopy"),
    ),
  tick(t) {
    if (t === 1)
      ask("table", "vs", tr("你刷完手進來扶鏡頭。", "Scrub, then come in and hold the camera."), {
        id: "cam0",
        text: tr("林主任：刷手、穿手術衣，來扶鏡頭", "Dr. Lin: scrub, gown, take the camera"),
        clearOn: "manual",
      });
    if (t === 2 && !S.anesAsked) {
      S.anesAsked = true;
      ask(
        "anes",
        "anes",
        tr(
          "你是 clerk？她最後一次吃東西是幾點？急診有寫嗎？",
          "You’re the clerk? When did she last eat? Did the ER write it down?",
        ),
        {
          id: "npo",
          text: tr("陳醫師問最後進食時間", "Dr. Chen asks when she last ate"),
          dlg: "anes",
        },
      );
    }
    if (t === 6) {
      S.asleep = true;
      eventBanner(tr("麻醉誘導", "Induction"), tr("陳醫師插管", "Dr. Chen intubates"));
    }
    if (t === 8) {
      S.phase = "timeout";
      eventBanner("TIME-OUT", tr("全員停下手邊的事", "Everyone stops what they’re doing"));
      if (inOR()) {
        S.timeout.withClerk = true;
        ask(
          "table",
          "circ",
          tr(
            "Time-out！大家停一下。clerk 你來確認：側別、備血、過敏。",
            "Time-out! Everyone stop. Clerk, you confirm: side, blood, allergies.",
          ),
          {
            id: "to",
            text: tr("佩佩：time-out 要你確認", "Pei-Pei: confirm the time-out"),
            dlg: "table",
            clearOn: "manual",
          },
        );
      } else
        post(
          "sys",
          "narr",
          null,
          tr("房間裡在做 time-out，你不在場。", "The room is doing the time-out, and you’re not there."),
        );
    }
    if (t === 11 && S.phase === "timeout") endTimeout();
    if (S.phase === "lap") {
      const rel = relNow(t);
      if (rel === 12) {
        const f = tr(
          "腹腔內大量血塊與鮮血，估計約 1,200 mL；右側輸卵管壺腹部膨大破裂，持續滲血。",
          "Large volume of clot and fresh blood in the abdomen, about 1,200 mL; the ampulla of the right tube is distended and ruptured, still oozing.",
        );
        finding(tr("腹腔鏡", "Laparoscopy"), f);
        post("table", "narr", null, f);
        if (!S.timeout.withClerk)
          post("table", "npc", "vs", tr("剛剛 time-out 的時候你在哪？", "Where were you during the time-out?"));
      }
      const si = stepNow();
      if (si != null && si !== S.step) {
        S.step = si;
        post("table", "narr", null, STEPS[si]?.[3] ?? "");
      }
      setWorking(true);
      if (S.q && S.qAt != null && t >= S.qAt + 4) qMiss();
      if (S.cmd && t > S.cmd.t + 2) {
        S.cam.miss++;
        post("table", "npc", "vs", tr("鏡頭呢？你在幹嘛？", "Camera? What are you doing?"));
        S.cmd = null;
        clearWait("cam");
      }
      if (CMDS[rel]) issueCmd(rel);
      const q = QS[rel];
      if (q) askQ(q);
      if (S.r2Answer?.t === t) {
        post("table", "npc", "r2", S.r2Answer.a);
        post("table", "npc", "vs", tr("嗯。", "Mm."));
        S.r2Answer = null;
      }
      if (rel === 15)
        post(
          "anes",
          "alert",
          "anes",
          tr("BP 82/45，佩佩，血拿上來掛！", "BP 82/45. Pei-Pei, bring the blood up and hang it!"),
        );
      if (rel === 16) post("circ", "npc", "circ", tr("血來了，第一袋掛上去了。", "Blood’s here. First unit is up."));
      if (rel === 26) {
        S.salpDone = true;
        finding(
          tr("術式", "Procedure"),
          tr("右側輸卵管切除完成；左側輸卵管外觀正常。", "Right salpingectomy complete; the left tube looks normal."),
        );
      }
      if (rel === 30) {
        S.phase = "count";
        S.count.at = t;
        S.cmd = null;
        clearWait("cam");
        eventBanner(
          tr("計數不符", "Count discrepancy"),
          tr("紗布 開 10／數 9", "Sponges: opened 10 / counted 9"),
          true,
        );
        post(
          "table",
          "alert",
          "scrub",
          tr(
            "紗布數目不對！開 10 塊，現在只有 9 塊。主任，先不要關！",
            "The sponge count is off! Ten opened, only nine now. Dr. Lin, don’t close yet!",
          ),
        );
        addWait({
          id: "count",
          text: tr("紗布少一塊", "A sponge is missing"),
          urgent: true,
          icon: tr("！", "!"),
          clearOn: "manual",
        });
      }
    }
    if (S.phase === "count" && !S.count.resolved && S.count.at !== null) {
      const g = S.gauze;
      if (t === S.count.at + 3 && !S.xrayKnown) post("table", "npc", "vs", g.h1);
      if (t === S.count.at + 6 && !S.xrayKnown) post("table", "npc", "vs", g.h2);
      if (S.xrayAt === t) {
        S.xrayKnown = true;
        post(
          "table",
          "npc",
          "circ",
          tr(`X 光片出來了：紗布在${g.where}。`, `X-ray’s back: the sponge is in ${g.where}.`),
        );
        addWait({
          id: "findg",
          text: tr(
            "X 光說在" + g.where + "，用鏡頭找出來",
            "X-ray shows it in " + g.where + ". Find it with the camera",
          ),
          clearOn: "manual",
          icon: "◎",
        });
      }
      if (S.r2Search === t)
        resolveCount(
          tr(
            `小周用鏡頭找了一陣，在${g.where}找到一塊吸滿血的紗布。`,
            `Chou searches with the camera for a while and finds a blood-soaked sponge in ${g.where}.`,
          ),
          tr("請小周用鏡頭找", "had Chou search with the camera"),
        );
      if (t === S.count.at + 12)
        resolveCount(
          tr(
            `主任自己接過鏡頭，在${g.where}找到那塊紗布。`,
            `Dr. Lin takes the camera himself and finds the sponge in ${g.where}.`,
          ),
          tr("主任自己找到", "Dr. Lin found it himself"),
        );
    }
    if (S.phase === "close" && S.closeAt !== null && t === S.closeAt + 1) {
      setWorking(true);
      if (S.offerSuture && !S.sutured) {
        if (S.sterile)
          ask("table", "vs", tr("來，皮你縫。", "Here, you close the skin."), {
            id: "sut",
            text: tr("林主任要你縫皮", "Dr. Lin wants you to close the skin"),
            dlg: "table",
            clearOn: "manual",
          });
        else
          post(
            "table",
            "npc",
            "vs",
            tr(
              "說好讓你縫皮，你人呢？算了，小周縫。",
              "I said you could close the skin. Where are you? Forget it, Chou, close.",
            ),
          );
      }
    }
    if (
      S.phase === "close" &&
      S.closeAt !== null &&
      t === S.closeAt + (S.offerSuture && S.sterile && !S.sutured ? 5 : 3)
    ) {
      if (S.offerSuture && !S.sutured) {
        clearWait("sut");
        post("table", "npc", "vs", tr("等不到你，小周縫了。", "Couldn’t wait for you. Chou closed."));
      }
      setWorking(false);
      S.phase = "post";
      S.postAt = t;
      S.atCamera = false;
      S.sterile = false;
      closeZoom();
      eventBanner(tr("手術結束", "Case over"));
      ask(
        "table",
        "vs",
        tr(
          "關好了。你脫手術衣出去跟陳媽媽說手術結束、很順利，細節我等一下出去講。",
          "We’re closed. Take off your gown, go out and tell Mrs. Chen the surgery is over and went smoothly. I’ll come out in a bit to explain the details.",
        ),
        {
          id: "mom",
          text: tr("去跟陳媽媽說明", "Go update Mrs. Chen"),
          dlg: "mother",
        },
      );
    }
    if (S.phase === "post" && S.postAt !== null && t === S.postAt + 12 && !S.motherPost) ending();
  },
  timeup: () =>
    void playEnding(
      "Z",
      tr("交班", "Sign-out"),
      [
        {
          who: "circ",
          text: tr("大夜班的人來接了，你先下去吧。", "The night shift is here to take over. You can head out."),
        },
      ],
      tr("時間到，交班。", "Time’s up. Sign-out."),
    ),

  hud() {
    const step = curStep();
    const st =
      S.phase === "lap" && step && S.step != null
        ? tr(`腹腔鏡 ${S.step + 1}/${STEPS.length}：${step[1]}`, `Lap ${S.step + 1}/${STEPS.length}: ${step[1]}`)
        : PHASE_HUD[S.phase];
    return [
      [
        tr("你的狀態", "Status"),
        S.banned
          ? tr("被請下台", "Sent off the table")
          : S.sterile
            ? tr("無菌（手術衣＋手套）", "Sterile (gown + gloves)")
            : S.scrubbed
              ? tr("刷過手，還沒穿手術衣", "Scrubbed, not gowned yet")
              : tr("還沒刷手", "Not scrubbed yet"),
      ],
      [tr("手術", "Surgery"), st],
    ];
  },
  urgent: () => S.phase === "count",
  outcomeText: () => tr("手術結束", "Case over"),

  drawBelow(c, now) {
    if (!S.tableGone) {
      let oy = 0;
      if (S.tableAnim) {
        oy = ((now - S.tableAnim.t0) / 1000) * 2.5;
        if (oy > 8) {
          S.tableGone = true;
          oy = 8;
        }
      }
      drawOrTable(c, {
        x: 13,
        y: 4,
        oy,
        draped: S.draped,
        asleep: S.asleep && !S.tableAnim,
        hair: "#1d1a26",
        skin: "#f6dcc8",
      });
    }
    const mx = 14 * T;
    const my = 1 * T;
    c.fillStyle = "#5be38a";
    for (let i = 0; i < 6; i++) {
      const ph = (i + Math.floor(now / 90)) % 6;
      c.fillRect(mx + 5 + i, ph === 2 ? my + 3 : my + 4, 1, 1);
    }
    const tx = 16 * T;
    const ty = 3 * T;
    if (S.phase === "lap" || S.phase === "count") {
      c.fillStyle = Math.floor(now / 500) % 2 ? "#a8323a" : "#8a2a2a";
      c.fillRect(tx + 3, ty + 1, 10, 6);
    }
  },
  drawAbove(c, now) {
    if ((S.phase === "lap" || S.phase === "count" || S.phase === "close") && !S.tableAnim) {
      c.fillStyle = "rgba(255,255,230,.10)";
      c.beginPath();
      c.arc(13 * T + 8, 5 * T + 8, 26, 0, Math.PI * 2);
      c.fill();
      const k = curStep()?.[2] ?? null;
      if (S.phase === "lap" && k === "burn" && Math.floor(now / 180) % 3 === 0) {
        R(c, "#ffffff", 13 * T + 7, 5 * T + 4, 2, 2);
        R(c, "#ffd447", 13 * T + 10, 5 * T + 2, 1, 1);
      }
      if (S.phase === "lap") {
        const y = 5 * T + 2 - (Math.floor(now / 400) % 4);
        if (k === "burn") {
          c.fillStyle = "rgba(220,220,225,.6)";
          c.fillRect(13 * T + 6, y - 4, 4, 3);
        }
      }
    }
  },

  graderCase: tr(
    `病例：第二關的 19 歲林曉婷，右側子宮外孕破裂，緊急腹腔鏡右側輸卵管切除。clerk 的任務：刷手（之後手不能亂碰）、穿手術衣（之後碰到非無菌物品或走出刀房就算污染）、參與 time-out、扶腹腔鏡鏡頭聽主任指令（病人的右邊在畫面左邊）、回答主任的問題（答對兩題以上主任會讓 clerk 縫皮；答錯或不回答累積兩次，主任會改問 R2）、處理紗布計數不符（紗布藏在腹腔裡某處，每次位置不同；正確做法是不關腹、重數、自己用鏡頭系統性地找低處與旁溝、必要時照 X 光；把脂肪、血塊誤認成紗布算小失誤）、術後跟家屬說明。曉婷是成年人，術前拜託不要讓媽媽知道她懷孕；clerk 跟媽媽說明時應保護她的隱私，把細節留給主任並先取得病人同意。`,
    `Case: Lin Hsiao-Ting, 19, from Level 2, with a ruptured right ectopic pregnancy, undergoing emergency laparoscopic right salpingectomy. The clerk's tasks: scrub (after which the hands must not touch anything), gown (after which touching anything non-sterile or walking out of the OR counts as contamination), take part in the time-out, hold the laparoscope camera and follow the attending's commands (the patient's right is on the left of the screen), answer the attending's questions (two or more correct and the attending lets the clerk close the skin; two wrong or unanswered in total and the attending redirects questions to the R2), handle the sponge count discrepancy (the sponge is hidden somewhere in the abdomen, in a different spot each time; the right approach is not to close, to recount, to search the dependent areas and gutters systematically with the camera themselves, and to get an X-ray if needed; mistaking fat or a clot for the sponge is a minor error), and update the family after surgery. Hsiao-Ting is an adult and asked before surgery that her mother not learn she was pregnant; when talking to the mother the clerk should protect her privacy, leave the details to the attending, and get the patient's consent first.`,
  ),
  rubric: tr(
    [
      "無菌觀念：刷手、穿手術衣、污染次數",
      "Time-out：是否在場、側別與備血等回答是否正確",
      "扶鏡頭：跟上主任指令、分清楚左右",
      "回答主任的問題",
      "紗布計數不符的處理",
      "術前與病人、麻醉科的溝通",
      "對家屬的說明與病人隱私",
    ],
    [
      "Sterile technique: scrubbing, gowning, number of contaminations",
      "Time-out: present or not; correct answers on side, blood availability, etc.",
      "Camera: keeping up with the attending's commands, telling left from right",
      "Answering the attending's questions",
      "Handling the sponge count discrepancy",
      "Pre-op communication with the patient and anesthesia",
      "Updating the family and protecting the patient's privacy",
    ],
  ),
  facts: () =>
    tr(
      [
        `主任提問：${S.qRes.map((q) => JUDGE_LABEL[q.j]).join("、") || "無"}`,
        `縫皮：${S.sutured ? "有" : "沒有"}`,
        `刷手：${S.scrubbed || S.sterile ? "有" : "沒有"}`,
        `污染：${S.contam} 次`,
        `Time-out：${S.timeout.withClerk ? "在場" : "不在場"}`,
        `鏡頭：對 ${S.cam.hit}／看錯邊 ${S.cam.wrong}／漏 ${S.cam.miss}`,
        `計數：${S.count.by ?? "未解決"}`,
        `透露懷孕給媽媽：${S.disclosed ? "是" : "否"}`,
      ],
      [
        `Attending's questions: ${S.qRes.map((q) => JUDGE_LABEL[q.j]).join(", ") || "none"}`,
        `Closed skin: ${S.sutured ? "yes" : "no"}`,
        `Scrubbed: ${S.scrubbed || S.sterile ? "yes" : "no"}`,
        `Contaminations: ${S.contam}`,
        `Time-out: ${S.timeout.withClerk ? "present" : "absent"}`,
        `Camera: right ${S.cam.hit} / wrong side ${S.cam.wrong} / missed ${S.cam.miss}`,
        `Count: ${S.count.by ?? "unresolved"}`,
        `Told Mom about the pregnancy: ${S.disclosed ? "yes" : "no"}`,
      ],
    ),
  known,
};
